/**
 * Which page of the Display menu each list was on when it last closed.
 *
 * The Display menu has a main page (Layout, Group by, "Columns…") and a
 * Columns page. If a person closes the menu while on the Columns page,
 * opening Display again goes straight back to the Columns page, so getting
 * back to the columns is one click. Closing on the main page reopens on the
 * main page.
 *
 * The choice is kept per list, in memory for the session, and copied to
 * localStorage so it survives a reload. Storage is optional: when it is
 * missing or throws, as in a private window, the in-memory copy still works.
 */
export type DisplayMenuPage = 'main' | 'columns';

const STORAGE_KEY_PREFIX = 'display-menu-page:v1:';

const pageByList = new Map<string, DisplayMenuPage>();

function storageKeyFor(listKey: string): string {
    return `${STORAGE_KEY_PREFIX}${listKey}`;
}

/** The page the list's Display menu should open on. A list never seen opens on the main page. */
export function rememberedDisplayMenuPage(listKey: string): DisplayMenuPage {
    const inMemory = pageByList.get(listKey);
    if (inMemory) {
        return inMemory;
    }
    try {
        const storedPage = localStorage.getItem(storageKeyFor(listKey));
        if (storedPage === 'columns' || storedPage === 'main') {
            pageByList.set(listKey, storedPage);
            return storedPage;
        }
    } catch {
        // No storage. The list opens on the main page.
    }
    return 'main';
}

export function rememberDisplayMenuPage(listKey: string, page: DisplayMenuPage): void {
    pageByList.set(listKey, page);
    try {
        localStorage.setItem(storageKeyFor(listKey), page);
    } catch {
        // No storage. The in-memory copy still lasts the session.
    }
}

/** For tests: forget every list's page, in memory and in storage. */
export function forgetDisplayMenuPages(): void {
    for (const listKey of pageByList.keys()) {
        try {
            localStorage.removeItem(storageKeyFor(listKey));
        } catch {
            // No storage to clear.
        }
    }
    pageByList.clear();
}
